(() => {
  if (window.WohnjoyRoomSelection) return;
  const collectionHandle = value => {
    try {
      const url = new URL(value || '', location.origin);
      return url.origin === location.origin ? decodeURIComponent(url.pathname.match(/\/collections\/([^/]+)/)?.[1] || '') : '';
    } catch { return ''; }
  };
  const roomsFor = config => (config.rooms || []).filter(Boolean).map(room => ({ ...room, categories: (room.categories || []).filter(Boolean).filter((category, index, all) => all.findIndex(other => other.handle === category.handle) === index) }));
  const selectRoom = config => {
    const memberships = new Set(config.memberships || []);
    const rooms = roomsFor(config).filter(room => room.categories.some(category => memberships.has(category.handle)));
    for (const context of [config.context, collectionHandle(location.href), collectionHandle(document.referrer)]) {
      if (!context || !memberships.has(context)) continue;
      const match = rooms.find(room => room.categories.some(category => category.handle === context));
      if (match) return match;
    }
    return rooms[0];
  };
  class WohnjoyRoomSelection {
    static selectRoom = selectRoom;
    constructor(signal, section = 'wohnjoy-room-candidates') {
      this.signal = signal;
      this.section = section;
      this.pages = new Map();
    }
    async fetchPage(value) {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !collectionHandle(url.href)) throw new Error('Invalid collection');
      url.searchParams.set('section_id', this.section);
      url.searchParams.set('filter.v.availability', '1');
      const key = url.href;
      if (this.pages.has(key)) return this.pages.get(key);
      const pending = this.requestPage(url);
      this.pages.set(key, pending);
      try { return await pending; } catch (error) { this.pages.delete(key); throw error; }
    }
    async requestPage(url) {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const signal = AbortSignal.any([this.signal, AbortSignal.timeout(15000)]);
          const response = await fetch(url, { signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Collection unavailable');
          const root = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-room-candidates]');
          if (!root) throw new Error('Missing collection section');
          const pages = Number(root.dataset.pages) || 1;
          const first = new URL(url);
          first.searchParams.set('page', '1');
          return { cards: [...root.querySelectorAll('template[data-room-candidate]')], next: root.dataset.next || (pages > 1 ? first.href : ''), current: Number(root.dataset.page) || 1, pages, items: Number(root.dataset.items) || 0 };
        } catch (error) {
          if (this.signal.aborted || attempt === 1) throw error;
        }
      }
    }
    async randomPage(category) {
      const first = await this.fetchPage(category.url);
      if (first.pages <= 1 || !first.items) return first;
      const page = Math.floor(Math.floor(Math.random() * first.items) / 12) + 1;
      if (page === 1) return first;
      const url = new URL(category.url, location.origin);
      url.searchParams.set('page', String(page));
      return this.fetchPage(url.href);
    }
    async pick(category, first, used, excluded) {
      let page = first;
      const visited = new Set();
      while (page && !this.signal.aborted) {
        if (visited.has(page.current)) return null;
        visited.add(page.current);
        const cards = [...page.cards];
        for (let index = cards.length - 1; index > 0; index--) {
          const other = Math.floor(Math.random() * (index + 1));
          [cards[index], cards[other]] = [cards[other], cards[index]];
        }
        for (const card of cards) {
          const id = String(card.dataset.productId);
          const memberships = JSON.parse(card.dataset.memberships || '[]');
          if (used.has(id) || memberships.some(handle => excluded.has(handle))) continue;
          used.add(id);
          return card;
        }
        if (!page.next) return null;
        page = await this.fetchPage(page.next);
      }
      return null;
    }
    async collect(config, append) {
      const items = config.items || [{ id: config.productId, memberships: config.memberships || [] }];
      const used = new Set(items.map(item => String(item.id)));
      const memberships = new Set(items.flatMap(item => item.memberships || []));
      const rooms = items.map(item => selectRoom({ ...config, memberships: item.memberships })).filter(Boolean);
      const excluded = new Set(rooms.flatMap(room => room.categories).filter(category => memberships.has(category.handle)).map(category => category.handle));
      const groups = rooms.map(room => room.categories.filter(category => !excluded.has(category.handle)));
      const categories = [];
      const handles = new Set();
      for (let index = 0; index < Math.max(0, ...groups.map(group => group.length)); index++) {
        for (const group of groups) {
          const category = group[index];
          if (!category || handles.has(category.handle)) continue;
          handles.add(category.handle);
          categories.push(category);
        }
      }
      let count = 0;
      for (let index = 0; index < categories.length; index += 3) {
        const batch = categories.slice(index, index + 3);
        const pages = await Promise.allSettled(batch.map(category => this.randomPage(category)));
        for (let offset = 0; offset < batch.length; offset++) {
          if (this.signal.aborted) return count;
          if (pages[offset].status !== 'fulfilled') continue;
          try {
            const card = await this.pick(batch[offset], pages[offset].value, used, excluded);
            if (card && !this.signal.aborted) { append(card, batch[offset]); count++; }
          } catch { if (this.signal.aborted) return count; }
        }
      }
      const minimum = Math.max(4, Math.min(8, Number(config.fallback?.count) || 4));
      if (count < minimum && config.fallback?.url && !this.signal.aborted) {
        try {
          const page = await this.randomPage(config.fallback);
          while (count < minimum && !this.signal.aborted) {
            const card = await this.pick(config.fallback, page, used, new Set());
            if (!card || this.signal.aborted) break;
            append(card, config.fallback);
            count++;
          }
        } catch { if (this.signal.aborted) return count; }
      }
      return count;
    }
  }
  window.WohnjoyRoomSelection = WohnjoyRoomSelection;
})();
